import { useEffect, useState } from "react";
import {
	type AnimalFilterId,
	animalFilters,
	fallbackAnimals,
	fetchAnimals,
	filterAnimals,
	formatAnimalLabel,
	formatAnimalName,
} from "./animal-data";
import { SiteChrome } from "./components/SiteChrome";

const colorClasses = ["c0", "c1", "c2", "c3", "c5", "c6", "c7", "c4"];
const backgroundClasses = ["", "dotbg", "", "", "stripebg", "", "dotbg", ""];
const bigColumnClasses = [
	"big-col-4",
	"big-col-1",
	"big-col-5",
	"big-col-2",
	"big-col-3",
];

/**
 * Render the /animals page as the dense gallery from the reference HTML.
 */
export const AnimalsPage = () => {
	const [animals, setAnimals] = useState(fallbackAnimals);
	const [activeFilterId, setActiveFilterId] = useState<AnimalFilterId>("all");
	const [searchQuery, setSearchQuery] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;

		fetchAnimals()
			.then((fetchedAnimals) => {
				if (!isMounted) {
					return;
				}

				setAnimals(fetchedAnimals);
				setErrorMessage(null);
			})
			.catch(() => {
				if (isMounted) {
					setErrorMessage(
						"Live animal index unavailable. Showing bundled animals.",
					);
				}
			});

		return () => {
			isMounted = false;
		};
	}, []);

	const filteredAnimals = filterAnimals(animals, activeFilterId, searchQuery);
	const totalAnimalCount = animals.length;
	let bigTileCount = 0;
	const animalTiles = filteredAnimals.map((animal, index) => {
		const isBig = index % 9 === 4;
		const colorClass = colorClasses[index % colorClasses.length] ?? "c0";
		const backgroundClass =
			backgroundClasses[index % backgroundClasses.length] ?? "";
		const bigColumnClass = isBig
			? (bigColumnClasses[bigTileCount % bigColumnClasses.length] ??
				"big-col-1")
			: "";

		if (isBig) {
			bigTileCount += 1;
		}

		return {
			animal,
			className: `tile ${colorClass} ${backgroundClass} ${isBig ? "big" : ""} ${bigColumnClass}`,
			index,
		};
	});

	return (
		<>
			<SiteChrome activePage="animals" />
			<main className="page active">
				<section className="gallery-wrap">
					<div className="gallery-hero">
						<div>
							<div className="sticker-txt gallery-sticker">
								All {totalAnimalCount} animals
							</div>
							<h1>
								Every <em>single</em>
								<br />
								one of them.
							</h1>
						</div>
						<div className="count">
							Showing
							<big>{filteredAnimals.length}</big>
							of {animals.length}
						</div>
					</div>

					<div className="filters">
						<span className="label">Filter:</span>
						{animalFilters.map((filter) => (
							<button
								key={filter.id}
								type="button"
								className={`chip ${activeFilterId === filter.id ? "active" : ""}`}
								onClick={() => setActiveFilterId(filter.id)}
							>
								{filter.label}
							</button>
						))}
						<input
							className="search"
							type="search"
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="search by name..."
						/>
					</div>

					{errorMessage ? (
						<p className="gallery-error">{errorMessage}</p>
					) : null}

					<div className="gallery-grid">
						<div className="manifesto-tile">
							<h4>
								The <em>full</em> zoo
							</h4>
							<div className="quote">
								"The animal kingdom is proof that life is both profound and
								ridiculous: the eagle contemplates the heavens, the whale sings
								into the abyss, and the raccoon is absolutely certain your trash
								holds the meaning of existence."
							</div>
							<div className="foot">
								Scroll · hover · pick · download · brag
							</div>
						</div>
						<div className="stat-tile">
							<div className="k">Total species</div>
							<div className="v">
								{filteredAnimals.length}/{totalAnimalCount}
							</div>
						</div>

						{animalTiles.map(({ animal, className, index }) => (
							<a
								key={animal.imageUrl}
								className={className}
								href={`/?animal=${encodeURIComponent(animal.animal)}#spotlight`}
								aria-label={`Feature ${formatAnimalLabel(animal.animal)}`}
							>
								<span className="tag">
									{String(index + 1).padStart(2, "0")}
								</span>
								<img
									src={animal.imageUrl}
									alt={`${formatAnimalLabel(animal.animal)} avatar`}
									loading="lazy"
								/>
								<span className="name">{formatAnimalName(animal.animal)}</span>
							</a>
						))}
					</div>
				</section>

				<footer>
					<h4>
						That's <em>every</em>
						<br />
						one. pick one.
					</h4>
					<div className="foot-row">
						<div>© {new Date().getFullYear()} AVATARANIMALS.COM</div>
						<div>{totalAnimalCount} SPECIES · FREE TO USE</div>
						<div>MADE WITH AI</div>
					</div>
				</footer>
			</main>
		</>
	);
};
